import React, { createContext, useContext, useEffect, useState } from "react"
import { useAuthContext } from "AuthContextWrapper"
import { documentsAPI } from "apis"
import { UserDocument } from "models/UserDocument"

export const documentTypeIdsToNames = [
  "", // don't use this, there is no document type with id 0
  "Other",
  "Estate",
  "Death certificate",
  "Will",
  "Bill / Invoice",
  "Financial Statement",
  "Legal Document",
  "Correspondances",
  "Photo Verification",
  "Driver’s License",
  "Passport",
  "Military I.D.",
  "Tribal Card",
  "Letters Testamentary",
]

export const DOC_TYPE_ID_TRIBAL_CARD = 13
export const DOC_TYPE_ID_PHOTO_VERIFICATION = 9
export const DOC_TYPE_ID_PASSPORT = 11
export const DOC_TYPE_ID_DRIVERS_LICENSE = 10
export const DOC_TYPE_ID_MILITARY_ID = 12
export const DOC_TYPE_ID_WILL = 4
export const DOC_TYPE_ID_LETTERS_TESTAMENTARY = 14
export const DOC_TYPE_ID_DEATH_CERTIFICATE = 3

interface Props {
  children: JSX.Element
}

export interface FileCabinetContextInterface {
  docs: UserDocument[]
  refreshDocs: () => Promise<void>
  setDocs: (docs: UserDocument[]) => any
  loadingDocs: boolean
  setLoadingDocs: (loading: boolean) => any
}

export const FileCabinetContext = createContext<FileCabinetContextInterface>({
  docs: [],
  loadingDocs: true,
  refreshDocs: async () => {},
  setDocs: () => undefined,
  setLoadingDocs: () => {},
})

export const FileCabinetContextWrapper = ({ children }: Props): JSX.Element => {
  const { activeEstate, user } = useAuthContext()
  const [docs, setDocs] = useState<UserDocument[]>([])
  const [loadingDocs, setLoadingDocs] = useState(true)

  useEffect(() => {
    loadAll()
  }, [activeEstate])

  const refreshDocs = async () => {
    loadAll(true)
  }

  const loadAll = async (skipLoading = false) => {
    if (!skipLoading) {
      setLoadingDocs(true)
    }
    if (!activeEstate?.id || !user?.id) {
      return
    }

    documentsAPI.getDocuments(activeEstate.id, user.id).then(async (myDocs: UserDocument[] | undefined) => {
      // Sort by doc creation date desc.  This will make it so newer docs
      // will consistently show up at the top
      myDocs =
        myDocs?.sort((d1, d2) => {
          if (!d2 || !d2.updatedAt) {
            console.warn("missing updated_at in document")
            return -1
          }
          if (!d1 || !d1.updatedAt) {
            console.warn("missing updated_at in document 2")
            return 1
          }
          return d1.updatedAt > d2.updatedAt ? 1 : -1
        }) || []

      myDocs = myDocs.filter((doc) => {
        return !doc.version?.title?.includes("[support-request-file]")
      })

      setDocs(myDocs)
      setLoadingDocs(false)
    })
  }

  return (
    <FileCabinetContext.Provider
      value={{
        docs,
        loadingDocs,
        refreshDocs,
        setDocs,
        setLoadingDocs,
      }}
    >
      {children}
    </FileCabinetContext.Provider>
  )
}

export function useFileCabinetContext(): FileCabinetContextInterface {
  const myContext = useContext(FileCabinetContext)
  if (!myContext) {
    throw new Error(
      "the component calling this file cabinet context is not nested within a context provider"
    )
  }

  return myContext
}
